import { useMemo } from "react";
import { ApprovalCard, type SerializableApprovalCard } from "~/components/tool-ui/approval-card";
import { HitlLoadError, HitlPending } from "./HitlStatus";
import { approvalWiring } from "./approvalWiring";
import type { HitlToolProps } from "./hitlTools";
import { parseToolBody } from "./parseToolBody";

// Args the backend `ask_for_approval` tool sends, mirroring tool-ui's
// SerializableApprovalCard minus the runtime fields we supply ourselves
// (`id` from the tool-call id, `choice` from the tool result).
type ApprovalArgs = Omit<SerializableApprovalCard, "id" | "role" | "choice">;

// Parse the tool-call args; null while still streaming or if the payload
// lacks the one required field (`title`).
function parseApproval(args: string): ApprovalArgs | null {
  const view = parseToolBody(args);
  if (view.kind !== "json" || Array.isArray(view.value)) return null;
  const value = view.value as { title?: unknown };
  if (typeof value.title !== "string" || value.title.length === 0) return null;
  return value as ApprovalArgs;
}

// Renders the imported tool-ui ApprovalCard for the deferred `ask_for_approval`
// call. The component owns its own interactive/receipt/executing states; we
// only feed it the parsed args and wire approve/deny back through `onAnswer`,
// which resumes the paused run. A rejected `onAnswer` re-enables the buttons
// (the component resets its executing lock in a finally), so retry just works.
// We opt into `collapsibleSections` so a many-field card (e.g. a Cohort Explorer
// config with dozens of columns) collapses behind a toggle instead of scrolling,
// matching the ingest card.
export const ApprovalCardTool = ({ entry, isRunning, onAnswer }: HitlToolProps) => {
  const card = useMemo(() => parseApproval(entry.args), [entry.args]);

  if (!card) {
    return isRunning ? (
      <HitlPending>Preparing approval…</HitlPending>
    ) : (
      <HitlLoadError>Couldn&apos;t load this approval.</HitlLoadError>
    );
  }

  return (
    <ApprovalCard
      {...card}
      id={entry.id}
      collapsibleSections
      {...approvalWiring(entry, onAnswer)}
    />
  );
};
